import { Edgelink } from './edgelink'

import type { EdgelinkConfig } from './types'

/**
 * IIFE bundle entrypoint.
 *
 * `<script src="https://cdn.edgelink.me/sdk/v1/edgelink.iife.js" data-publishable-key="pk_live_xxx"></script>`
 *
 * 동작:
 *   1. data-publishable-key 속성에서 publishable key 추출
 *   2. window.edgelink가 stub queue로 미리 존재하면 큐를 drain
 *   3. 실제 Edgelink 인스턴스를 window.edgelink에 노출
 *
 * stub queue 패턴 (GA/Segment 표준):
 *   <script>
 *     ;(function () {
 *       var w = window
 *       if (w.edgelink) return
 *       var queue = (w.edgelink = function () { queue.q.push(arguments) })
 *       queue.q = []
 *     })()
 *   </script>
 *   <script async src="..."></script>
 */
interface QueuedCall {
  0: 'track' | 'identify' | 'init'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [index: number]: any
}

interface StubQueue {
  q?: QueuedCall[]
}

const findPublishableKey = (): string | null => {
  if (typeof document === 'undefined') return null
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[data-publishable-key]')
  for (const script of Array.from(scripts)) {
    const key = script.dataset.publishableKey
    if (key != null && key !== '') return key
  }
  return null
}

const apiBaseUrlFromScript = (): string | undefined => {
  if (typeof document === 'undefined') return undefined
  const script = document.querySelector<HTMLScriptElement>('script[data-publishable-key][data-api-base-url]')
  return script?.dataset.apiBaseUrl
}

/**
 * `data-cookie-domain` 속성을 읽어 `_el_cid` 쿠키의 도메인을 지정한다.
 * 예: `.edgelink.me` → `edgelink.me`와 `app.edgelink.me`가 쿠키를 공유(cross-subdomain 어트리뷰션).
 * 미지정 시 host-only 쿠키(기존 동작, 하위 호환).
 */
const cookieDomainFromScript = (): string | undefined => {
  if (typeof document === 'undefined') return undefined
  const script = document.querySelector<HTMLScriptElement>('script[data-publishable-key][data-cookie-domain]')
  return script?.dataset.cookieDomain
}

const bootstrap = (): void => {
  if (typeof window === 'undefined') return
  const publishableKey = findPublishableKey()
  if (publishableKey == null) {
    if (typeof console !== 'undefined') {
      console.warn('@edgelinkapp/sdk-web: data-publishable-key 속성이 누락되어 SDK 초기화를 건너뜁니다.')
    }
    return
  }

  const config: EdgelinkConfig = { publishableKey }
  const apiBaseUrl = apiBaseUrlFromScript()
  if (apiBaseUrl != null) config.apiBaseUrl = apiBaseUrl
  const cookieDomain = cookieDomainFromScript()
  if (cookieDomain != null) config.cookieDomain = cookieDomain

  const instance = new Edgelink(config)
  const existing = (window as unknown as { edgelink?: StubQueue }).edgelink
  const queuedCalls = existing?.q ?? []
  ;(window as unknown as { edgelink: Edgelink }).edgelink = instance
  instance.init()

  // stub queue drain
  for (const call of queuedCalls) {
    const method = call[0]
    if (method === 'track') instance.track(call[1], call[2])
    else if (method === 'identify') instance.identify(call[1], call[2])
  }
}

bootstrap()

// 테스트 전용 export (파싱 로직 단위 검증용). 런타임 진입점은 위 bootstrap() 자동 실행이다.
export { cookieDomainFromScript }
